<template>
  <container>
    <h1 style="font-size:25px;text-align:center;position:relative;">
      Firebase Stats
      <v-button
        class="refresh-button"
        @click.native="loadStats"
        :disabled="loading"
        title="Refresh"
        color="secondary"
        size="icon"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M1 4v6h6"/>
          <path d="M23 20v-6h-6"/>
          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
        </svg>
      </v-button>
    </h1>

    <div v-if="loading" style="text-align:center; padding: 2rem;">
      <p>Loading stats...</p>
    </div>

    <div v-if="error" style="text-align:center; padding: 2rem; color: red;">
      <p style="margin-bottom: 1rem;">{{ error }}</p>
      <v-button @click.native="loadStats">Retry</v-button>
    </div>

    <div v-if="!loading && !error">

      <!-- Billable Metrics (Google Cloud Monitoring) -->
      <div class="stats-section" v-if="billingStats">
        <div class="section-header">
           <h2>Billable Metrics (Last 30 Days)</h2>
        </div>
        <div class="stats-grid">
           
           <!-- Connections -->
           <div class="stat-card">
              <div class="stat-label">Connections</div>
              <div class="stat-value">{{ billingStats.connections }} / 200k</div>
              <div class="stat-value-small">Peak Concurrent</div>
           </div>

           <!-- Storage -->
           <div class="stat-card">
              <div class="stat-label">Storage</div>
              <div class="stat-value">{{ formatBytes(billingStats.storage) }}</div>
              <div class="stat-value-small">Current Total</div>
           </div>

           <!-- Downloads -->
           <div class="stat-card">
              <div class="stat-label">Downloads</div>
              <div class="stat-value">{{ formatBytes(billingStats.downloads) }}</div>
              <div class="stat-value-small">Total (30 Days)</div>
           </div>

        </div>
      </div>
      
      <!-- Collection Sizes -->
      <div class="stats-section">
        <div class="section-header">
          <h2>Collection Sizes (Approximated by Item Count)</h2>
          <p class="section-hint">Click a collection to explore its objects</p>
        </div>
        <div class="stats-grid">
          <div
            v-for="stat in collectionStats"
            :key="stat.name"
            class="stat-card stat-card--clickable"
            :class="{ 'stat-card--active': activeCollection === stat.name }"
            @click="openExplorer(stat.name)"
          >
            <div class="stat-label">{{ stat.name }}</div>
            <div class="stat-value">{{ stat.count }}</div>
            <div class="stat-value-small" v-if="stat.estimatedSize !== 'N/A'">{{ stat.estimatedSize }}</div>
            <div class="stat-error" v-if="stat.error">Error: {{ stat.error }}</div>
          </div>
        </div>
      </div>

      <!-- Collection Explorer Panel -->
      <div class="stats-section explorer-panel" v-if="activeCollection">
        <div class="section-header explorer-header">
          <h2>Exploring: <strong>{{ activeCollection }}</strong></h2>
          <v-button color="secondary" size="icon" @click.native="closeExplorer" title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </v-button>
        </div>

        <!-- ID Lookup Form -->
        <div class="explorer-form">
          <input
            id="explorer-id-input"
            class="explorer-input"
            type="text"
            v-model="explorerObjectId"
            placeholder="Enter object ID..."
            :disabled="explorerLoading"
            @keyup.enter="fetchObject"
          />
          <v-button
            @click.native="fetchObject"
            :disabled="explorerLoading || !explorerObjectId.trim()"
          >
            {{ explorerLoading ? 'Fetching...' : 'Fetch' }}
          </v-button>
        </div>

        <!-- Fetch Error -->
        <p class="explorer-error" v-if="explorerError">{{ explorerError }}</p>

        <!-- Fetched Data Block -->
        <div class="explorer-data" v-if="explorerData !== null">
          <div class="explorer-data-header">
            <span class="explorer-data-title">{{ activeCollection }} / {{ explorerObjectId }}</span>
            <!-- Trash icon (shown when not confirming) — Feather-style SVG -->
            <button
              v-if="!confirmingDelete"
              class="delete-icon-btn"
              @click="confirmingDelete = true"
              title="Delete this object"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                <path d="M10 11v6"/>
                <path d="M14 11v6"/>
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
            </button>
          </div>

          <!-- Inline Delete Confirmation -->
          <div class="delete-confirm-row" v-if="confirmingDelete">
            <span class="delete-confirm-text">Are you sure? This cannot be undone.</span>
            <v-button
              color="danger"
              @click.native="confirmDelete"
              :disabled="deleteLoading"
            >
              {{ deleteLoading ? 'Deleting...' : 'Yes, Delete' }}
            </v-button>
            <v-button
              color="secondary"
              @click.native="confirmingDelete = false"
              :disabled="deleteLoading"
            >
              Cancel
            </v-button>
          </div>

          <!-- Delete error -->
          <p class="explorer-error" v-if="deleteError">{{ deleteError }}</p>

          <!-- Delete success -->
          <p class="explorer-success" v-if="deleteSuccess">Deleted successfully.</p>

          <!-- Raw JSON -->
          <pre class="explorer-pre" v-if="!deleteSuccess">{{ JSON.stringify(explorerData, null, 2) }}</pre>
        </div>
      </div>

    </div>
  </container>
</template>

<script>
import Container from "@/components/Container"
import VButton from "@/components/Forms/VButton"
import FirebaseStatsService from "@/services/FirebaseStatsService"

export default {
  name: "firebase-stats",
  data: function() {
    return {
      loading: false,
      error: null,
      collectionStats: [],
      billingStats: null,
      // Explorer state
      activeCollection: null,
      explorerObjectId: "",
      explorerLoading: false,
      explorerError: null,
      explorerData: null,
      confirmingDelete: false,
      deleteLoading: false,
      deleteError: null,
      deleteSuccess: false
    }
  },
  components: {
    Container,
    VButton
  },
  methods: {
    formatBytes: function(bytes, decimals) {
        decimals = decimals !== undefined ? decimals : 2;
        if (!+bytes) return '0 Bytes'
        var k = 1024
        var dm = decimals < 0 ? 0 : decimals
        var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        var i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
    },
    loadStats: function() {
      var self = this;
      self.loading = true
      self.error = null
      Promise.all([
           FirebaseStatsService.getCollectionStats(),
           FirebaseStatsService.getUsageStats()
      ]).then(function(results) {
        var collRes = results[0];
        var billRes = results[1];
        if (collRes.data && collRes.data.success) {
            self.collectionStats = collRes.data.data
        }
        if (billRes.data && billRes.data.success) {
            self.billingStats = billRes.data.data
        }
      }).catch(function(err) {
        self.error = err.message || 'Failed to connect to server'
      }).then(function() {
        self.loading = false
      });
    },
    openExplorer: function(collectionName) {
      if (this.activeCollection === collectionName) return;
      this.activeCollection = collectionName;
      this.explorerObjectId = "";
      this.explorerError = null;
      this.explorerData = null;
      this.confirmingDelete = false;
      this.deleteError = null;
      this.deleteSuccess = false;
      var self = this;
      self.$nextTick(function() {
        var input = document.getElementById('explorer-id-input');
        if (input) input.focus();
      });
    },
    closeExplorer: function() {
      this.activeCollection = null;
      this.explorerObjectId = "";
      this.explorerError = null;
      this.explorerData = null;
      this.confirmingDelete = false;
      this.deleteError = null;
      this.deleteSuccess = false;
    },
    fetchObject: function() {
      var id = this.explorerObjectId.trim();
      if (!id) return;
      var self = this;
      self.explorerLoading = true;
      self.explorerError = null;
      self.explorerData = null;
      self.confirmingDelete = false;
      self.deleteError = null;
      self.deleteSuccess = false;
      FirebaseStatsService.getObjectById(self.activeCollection, id).then(function(res) {
        if (res.data && res.data.success) {
          self.explorerData = res.data.data;
        } else {
          self.explorerError = "Unexpected response from server.";
        }
      }).catch(function(err) {
        var msg = "Object not found";
        if (err.response && err.response.data && err.response.data.message) {
          msg = err.response.data.message;
        } else if (err.message) {
          msg = err.message;
        }
        self.explorerError = msg;
      }).then(function() {
        self.explorerLoading = false;
      });
    },
    confirmDelete: function() {
      var self = this;
      self.deleteLoading = true;
      self.deleteError = null;
      FirebaseStatsService.deleteObject(self.activeCollection, self.explorerObjectId.trim()).then(function() {
        self.explorerData = null;
        self.confirmingDelete = false;
        self.deleteSuccess = true;
      }).catch(function(err) {
        var msg = "Delete failed.";
        if (err.response && err.response.data && err.response.data.message) {
          msg = "Delete failed: " + err.response.data.message;
        } else if (err.message) {
          msg = "Delete failed: " + err.message;
        }
        self.deleteError = msg;
        self.confirmingDelete = false;
      }).then(function() {
        self.deleteLoading = false;
      });
    }
  },
  created: function() {
    this.loadStats()
  }
}
</script>

<style scoped>
.stats-section {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1.5rem;
  margin: 20px 0;
  background: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, .05);
}

.section-header {
  border-bottom: 2px solid #eee;
  padding-bottom: 0.5rem;
  margin-bottom: 1.5rem;
}

.section-header h2 {
  font-size: 20px;
  color: #423799;
  margin: 0;
}

.section-hint {
  font-size: 12px;
  color: #999;
  margin: 4px 0 0 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.stat-card {
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #eee;
}

.stat-card--clickable {
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.stat-card--clickable:hover {
  border-color: #423799;
  background: #f4f2fd;
}

.stat-card--active {
  border-color: #423799;
  background: #ede9fb;
}

.stat-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #423799;
}

.stat-value-small {
  font-size: 14px;
  color: #888;
}

.stat-error {
  color: red;
  font-size: 12px;
}

/* Refresh button — sits absolute in the h1 */
.refresh-button {
  position: absolute !important;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

/* Explorer Panel */
.explorer-panel {
  border-color: #423799;
}

.explorer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.explorer-header h2 {
  margin: 0;
}

.explorer-form {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  align-items: center;
}

.explorer-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: monospace;
  height: 32px;
  box-sizing: border-box;
}

.explorer-input:focus {
  outline: none;
  border-color: #423799;
}

.explorer-error {
  color: #c0392b;
  font-size: 13px;
  margin: 0.5rem 0;
}

.explorer-success {
  color: #27ae60;
  font-size: 13px;
  margin: 0.5rem 0;
}

.explorer-data {
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.explorer-data-header {
  background: #f5f5f5;
  padding: 6px 12px;
  border-bottom: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.explorer-data-title {
  font-size: 12px;
  color: #555;
  font-family: monospace;
}

/* Feather-style inline SVG trash button */
.delete-icon-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 3px 4px;
  color: #aaa;
  line-height: 0;
  display: inline-flex;
  align-items: center;
  border-radius: 3px;
  transition: color 0.15s, background 0.15s;
}

.delete-icon-btn:hover {
  color: #c0392b;
  background: #ffeaea;
}

.delete-confirm-row {
  background: #fff8f8;
  border-bottom: 1px solid #fcc;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.delete-confirm-text {
  font-size: 13px;
  color: #c0392b;
  flex: 1;
}

.explorer-pre {
  margin: 0;
  padding: 1rem;
  font-size: 12px;
  background: #fafafa;
  overflow: auto;
  max-height: 400px;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>



// WEBPACK FOOTER //
// FirebaseStats.vue?1b80aa66